import { Button, Table } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PermissionsRequired } from '@/containers/permission-containers'
import type { GetSpecificConditionsData } from '@/modules/activities'
import { GET_SPECIFIC_CONDITIONS } from '@/modules/activities'
import type { ExportForm } from '@/modules/exports'
import { Attachments } from '@/modules/issues'
import { format } from '@/utils/date-fns'
import type { FileAttachment } from '@/utils/hooks'
import {
  useBreadcrumbs,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useQuery } from '@apollo/client'
import { Icon, Loader, usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { addDays, formatISO, parseISO, startOfDay } from 'date-fns'
import React, { useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import type { ArchivedInfringementForm } from '..'
import {
  INFRINGEMENT_ACTIVITIES_EXTEND_AFTER_MINUTES,
  INFRINGEMENT_ACTIVITIES_EXTEND_BEFORE_MINUTES,
  INFRINGEMENT_LEADER_PERMISSIONS,
  INFRINGEMENT_QUERY,
  InfringementDetailV1,
  PATCH_INFRINGEMENT_MUTATION,
  useInfringementsMutations,
} from '..'
import { useInfringementCategoryDescription } from '../category-descriptions'
import { MainInfo } from '../components'
import { Details } from '../components/Details'
import { ExportInfringementsModal } from '../components/ExportInfringementsModal'
import { ReceiptInfringementModal } from '../components/ReceiptInfringementModal'
import { ArchiveInfringementPrompt } from '../components/table/ArchiveInfringementModal'
import type { InfringementQueryInfringement } from '../types.graphl'
import { H1, H2 } from '@/components'
import { getNodes } from '@/utils/graphql'

type FerrySpecificConditionType = 'FERRY' | 'FERRY_START' | 'FERRY_END'

const FERRY_SPECIFIC_CONDITION_TYPES: ReadonlySet<string> =
  new Set<FerrySpecificConditionType>(['FERRY', 'FERRY_START', 'FERRY_END'])

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  /* TODO this is invalid, should it be enabled? */
  s header {
    display: flex;
    justify-content: space-between;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: ${isMobile ? 'column' : 'row'};
  gap: 1rem;

  margin: 1rem 0;
  padding: 0 1rem;

  button {
    height: 3rem;
  }
`

const Area = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;

  header {
    display: flex;

    gap: 1rem;
    align-items: center;
    margin: 1rem 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const ExportArchiveButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${isMobile ? 'space-between' : 'flex-end'};
  flex-direction: ${isMobile ? 'column' : 'row'};
  gap: 1rem;
`
const Content = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow-x: auto;
`

const NoOpenIssueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.dark};
`

const NoOpenIssueText = styled.span`
  color: ${props => props.theme.colors.text.dark};
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: default;
`

interface InfringementProps {
  listPath?: string
}

export const Infringement: React.FC<InfringementProps> = ({
  listPath = '/infringements',
}) => {
  const categoryDescription = useInfringementCategoryDescription()
  const translations = useTranslate({
    infringement: 'common.infringement',
    loading: 'common.loading-ellipsis',
    activities: 'common.activities',
    seeInActivities: 'infringements.see-in-activities',

    actionLogTitle: 'issues.action-log',

    open: 'common.open-alt',
    close: 'common.close',
    reopen: 'common.reopen',
    closed: 'common.closed',
    archived: ['common.archived', val => val.toLowerCase()],

    date: 'common.date',
    about: 'common.about',

    export: 'common.export',
    archive: 'common.archive',
    restore: 'common.restore',
    relatedIssues: 'infringements.related-issues',

    receipt: 'infringements.receipt',
    action: 'common.action',
    timestamp: 'common.timestamp',

    event: 'common.event',
    ferryEvents: 'infringements.ferry-events',
    ferryEvent: 'infringements.ferry-event',
    ferryStart: 'infringements.ferry-start',
    ferryEnd: 'infringements.ferry-end',

    actionLog: {
      CREATED: ['issues.created-by-user', { user: 'user' }],
      UPDATED: ['issues.updated-by-user', { user: 'user' }],

      ARCHIVED: ['issues.archived-by-user', { user: 'user' }],
      RESTORED: ['issues.restored-by-user', { user: 'user' }],

      CLOSED: ['issues.closed-by-user', { user: 'user' }],
      REOPENED: ['issues.reopened-by-user', { user: 'user' }],

      RECEIPTED: ['issues.receipted-by-user', { user: 'user' }],

      LEADER_COMMENT_UPDATED: [
        'issues.leader-comment-updated-by-user',
        { user: 'user' },
      ],

      REMINDER: [
        'issues.reminder-by-user-to-user',
        { user: 'user', target: 'target' },
      ],
    },
  })

  const navigate = useNavigate()

  const infringementId = useParam('infringementId')

  const { exportInfringement, loading } = useInfringementsMutations()
  const { setOverride } = useBreadcrumbs()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canChange = hasPermissions(INFRINGEMENT_LEADER_PERMISSIONS, true)
  const [activitiesDate, setActivitiesDate] = useState<Date | null>(null)

  const form = useForm({
    values: {
      leadersComment: '',
    },
  })

  const { data, loading: queryLoading } = useQuery(INFRINGEMENT_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: infringementId,
      activitiesExtendBeforeMinutes:
        INFRINGEMENT_ACTIVITIES_EXTEND_BEFORE_MINUTES,
      activitiesExtendAfterMinutes:
        INFRINGEMENT_ACTIVITIES_EXTEND_AFTER_MINUTES,
    },
    onCompleted(data) {
      if (!data.infringement) void navigate(listPath)

      const { infringement } = data
      const date = format(new Date(infringement.createdAt), 'PP')
      setOverride(infringement.id, date)
      setActivitiesDate(parseISO(infringement.infringementTimestamp))

      if (!form.formEdited) {
        const values = { leadersComment: infringement.leadersComment || '' }
        form.updateForm(values)
        form.updateInitialValues(values)
      }
    },
    onError: onErrorAuto(),
  })

  const infringement = data?.infringement
  const attachments = getNodes(infringement?.attachments)
  const infringementTimestamp = infringement
    ? parseISO(infringement?.infringementTimestamp)
    : undefined
  const isClosed = infringement?.closedAt !== null
  const userId = infringement?.user.id ?? me.id
  // Drivers acknowledge ("kvitter") their own infringement without the change
  // permission; the leader's comment and closing stay leader actions.
  const canReceipt = canChange || infringement?.user.id === me.id

  const timestamp = activitiesDate ? activitiesDate : new Date()
  const tempDate = startOfDay(timestamp)

  const { data: specificConditionsData } = useQuery<GetSpecificConditionsData>(
    GET_SPECIFIC_CONDITIONS,
    {
      skip: !userId || !infringement,
      variables: {
        userId,
        dateStart: formatISO(tempDate),
        dateEnd: formatISO(addDays(tempDate, 1)),
      },
      onError: onErrorAuto(),
    }
  )
  const [patchInfringement] = useMutation(PATCH_INFRINGEMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Infringement'],
    onError: onErrorAuto(),
  })

  const [receiptInfringement] = useMutation(PATCH_INFRINGEMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Infringement', 'AllInfringements', 'AllIssues'],
    onError: onErrorAuto(),
  })

  const [editing, setEditing] = useState(false)

  const isLoading = queryLoading || loading || !data

  const articles = infringement?.infringementCategory?.legalBases?.edges.map(
    edge => edge?.node?.article ?? ''
  )

  const startDate = infringement?.periodStartDate
    ? parseISO(infringement?.periodStartDate)
    : new Date()
  const endDate = infringement?.periodEndDate
    ? parseISO(infringement?.periodEndDate)
    : new Date()
  const specificConditions = useMemo(
    () => getNodes(specificConditionsData?.allSpecificConditions),
    [specificConditionsData?.allSpecificConditions]
  )

  async function onEditLeadersComment() {
    setEditing(false)
    void patchInfringement({
      variables: {
        id: infringementId!,
        input: {
          leadersComment: form.formValues.leadersComment,
        },
      },
    })
  }

  async function handleArchiveInfringement(
    infringement?: InfringementQueryInfringement
  ) {
    if (typeof infringement === 'undefined') return

    if (infringement.archivedAt !== null) {
      void executeArchiveInfringement(infringement)()
      return
    }

    const initialData: ArchivedInfringementForm = {
      archivedBasis: '',
    }

    const { data } = await addPrompt<ArchivedInfringementForm | null>(
      resolve => (
        <ArchiveInfringementPrompt
          initialData={initialData}
          onResolve={resolve}
        />
      )
    )
    if (data === null) return

    void executeArchiveInfringement(infringement)(data)
  }

  function executeArchiveInfringement(
    infringement?: InfringementQueryInfringement | null
  ) {
    if (!infringement) return () => void 0

    return async (data?: ArchivedInfringementForm) => {
      const isArchived = infringement.archivedAt !== null
      await patchInfringement({
        variables: {
          id: infringement.id,
          input: {
            archivedBy: me.id,
            archivedAt: isArchived ? null : formatISO(new Date()),
            archivedBasis: data?.archivedBasis,
          },
        },
      })
    }
  }

  async function handleExport() {
    if (!infringement) return

    const { data } = await addPrompt<ExportForm | null>(resolve => (
      <ExportInfringementsModal onSubmit={resolve} />
    ))
    if (!data) return

    const recipients = data.recipients.length === 0 ? [me.id] : data.recipients

    void exportInfringement({
      variables: {
        infringementId: infringementId,
        exportOption: data.exportOption,
        recipientId: me.id,
        recipientEmails: recipients,
      },
    })
  }

  async function handleReceiptInfringement() {
    const { data } = await addPrompt<{
      reason: string
      attachmentsAdd: Omit<FileAttachment, 'id'>[]
      attachmentsRemove: string[]
    } | null>(resolve => (
      <ReceiptInfringementModal
        initialData={{
          reason: infringement?.issueReason,
          attachments: attachments,
        }}
        onSubmit={resolve}
      />
    ))
    if (!data || !data.reason || !infringement) return

    await receiptInfringement({
      variables: {
        id: infringement.id,
        input: {
          solution: data.reason,
          attachmentsAdd: data.attachmentsAdd,
          attachmentsRemove: data.attachmentsRemove,
        },
      },
    })
  }

  async function handleCloseInfringement() {
    if (!infringement) return
    await patchInfringement({
      variables: {
        id: infringement.id,
        input: {
          closedAt: infringement.closedAt ? null : formatISO(new Date()),
          closedBy: me.id,
        },
      },
    })
  }

  useEffect(() => {
    if (data) return
    setOverride(infringementId, translations.loading)
  }, [data, infringementId, setOverride, translations.loading])

  const actionLogs = getNodes(infringement?.actionLogs).toReversed()
  const ferrySpecificConditions = useMemo(
    () =>
      specificConditions
        .filter(
          (
            condition
          ): condition is typeof condition & {
            type: FerrySpecificConditionType
          } => FERRY_SPECIFIC_CONDITION_TYPES.has(condition.type)
        )
        .slice()
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
    [specificConditions]
  )
  const ferryTypeLabels: Record<FerrySpecificConditionType, string> = {
    FERRY: translations.ferryEvent,
    FERRY_START: translations.ferryStart,
    FERRY_END: translations.ferryEnd,
  }

  return (
    <Wrapper>
      <Area>
        <header>
          <H1>{translations.infringement}</H1>
        </header>

        <Content>
          {isLoading && <Loader.Dots />}
          {infringement && (
            <>
              <MainInfo
                loading={queryLoading}
                category={infringement.infringementCategory}
                archivedAt={infringement?.archivedAt}
                timestamp={infringementTimestamp}
                startDate={startDate}
                endDate={endDate}
              />

              <Details
                loading={queryLoading}
                reasonLowerLimit={infringement.infringementCategory.lowerLimit}
                reason={categoryDescription(
                  infringement.infringementCategory?.identifier,
                  infringement.infringementCategory?.description
                )}
                reasonUpperLimit={infringement.infringementCategory?.upperLimit}
                userFullname={infringement.user?.fullName}
                reasonValue={infringement.reasonValue}
                archivedAt={infringement.archivedAt}
                archivedBasis={infringement.archivedBasis}
                solution={infringement.solution ?? infringement.issueReason}
                leadersComment={form.formValues.leadersComment}
                onEditLeadersComment={form.formChangeHandler('leadersComment')}
                onSubmitLeadersComment={() => onEditLeadersComment()}
                onStartEditing={() => setEditing(true)}
                editing={editing}
                canEdit={canChange}
                legalBasis={articles}
              />
              <Attachments
                editing={false}
                attachments={attachments}
                onUpdate={() => void 0}
              />
            </>
          )}
        </Content>

        {infringement && (
          <ButtonWrapper>
            <PermissionsRequired
              permissions={PERMISSIONS.companies.export.exportReports}
            >
              <Button
                disabled={isLoading || !infringement}
                padding="0 2rem"
                onClick={() => handleExport()}
              >
                <Icon icon="inbox-in" fixedWidth margin="0 6px 0 0" />
                {translations.export}
              </Button>
            </PermissionsRequired>

            <PermissionsRequired permissions={INFRINGEMENT_LEADER_PERMISSIONS}>
              <Button
                disabled={isLoading || !infringement}
                padding="0 2rem"
                onClick={() => handleArchiveInfringement(infringement)}
              >
                {infringement?.archivedAt === null && (
                  <Icon icon="archive" fixedWidth margin="0 6px 0 0" />
                )}
                {infringement?.archivedAt !== null
                  ? translations.restore
                  : translations.archive}
              </Button>
            </PermissionsRequired>
            <ExportArchiveButtons>
              {!isClosed ? (
                <>
                  {canReceipt && (
                    <Button
                      disabled={isLoading || !infringement}
                      padding="0 2rem"
                      onClick={() => handleReceiptInfringement()}
                    >
                      <Icon icon="receipt" fixedWidth margin="0 6px 0 0" />
                      {translations.receipt}
                    </Button>
                  )}
                  {canChange && (
                    <Button
                      disabled={isLoading || !infringement}
                      padding="0 2rem"
                      onClick={() => handleCloseInfringement()}
                    >
                      <Icon icon="check" fixedWidth margin="0 6px 0 0" />
                      {translations.close}
                    </Button>
                  )}
                </>
              ) : (
                <NoOpenIssueWrapper>
                  <Icon
                    icon="check"
                    color="green"
                    fixedWidth
                    margin="0 6px 0 0"
                  />
                  <NoOpenIssueText>{translations.closed}</NoOpenIssueText>
                  {canChange && (
                    <Button
                      disabled={isLoading || !infringement}
                      padding="0 2rem"
                      onClick={() => handleCloseInfringement()}
                    >
                      <Icon icon="check" fixedWidth margin="0 6px 0 0" />
                      {translations.reopen}
                    </Button>
                  )}
                </NoOpenIssueWrapper>
              )}
            </ExportArchiveButtons>
          </ButtonWrapper>
        )}
      </Area>

      {infringementId && !queryLoading && infringement && (
        <Area>
          <InfringementDetailV1
            infringement={infringement}
            ferrySpecificConditions={ferrySpecificConditions}
          />
        </Area>
      )}
      {ferrySpecificConditions.length > 0 && (
        <>
          <H2 className="mt-8">{translations.ferryEvents}</H2>
          <Table>
            <thead>
              <tr>
                <th>{translations.timestamp}</th>
                <th>{translations.event}</th>
              </tr>
            </thead>

            <tbody>
              {ferrySpecificConditions.map(node => (
                <tr key={node.id}>
                  <td>{format(new Date(node.timestamp), 'PPpp')}</td>
                  <td>{ferryTypeLabels[node.type]}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
      {actionLogs.length > 0 && (
        <>
          <H2 className="mt-8">{translations.actionLogTitle}</H2>
          <Table>
            <thead>
              <tr>
                <th>{translations.timestamp}</th>
                <th>{translations.action}</th>
              </tr>
            </thead>

            <tbody>
              {actionLogs.map(node => {
                const createdAt = format(new Date(node.createdAt), 'PPpp')
                const user = node.user ? node.user.fullName : 'System'
                const secondaryUser = node.secondaryUser
                  ? node.secondaryUser.fullName
                  : ''
                const actionString = node.secondaryUser
                  ? translations.actionLog.REMINDER({
                      user: user,
                      target: secondaryUser,
                    })
                  : translations.actionLog[node.action]({ user: user })

                return (
                  <tr key={node.id}>
                    <td>{createdAt}</td>
                    <td>{actionString}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </>
      )}
    </Wrapper>
  )
}
